import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as Rx from 'rxjs';
import { tap, map } from 'rxjs/operators';
import { AppSettingService } from '../app.service';
import { MediaAPIService } from '../media/media.service';
import { UserInfoService } from '../user/user-info.service';
import { CacheService } from '../utility/cache.service';

export interface IRolePermit {
	CanUpload: boolean;
	CanShare: boolean;
}

export interface IResourcePermit {
	SystemAdmin?: IRolePermit;
	ZoneAdmin: IRolePermit;
	SchoolAdmin: IRolePermit;
	Teacher: IRolePermit;
	Student: IRolePermit;
	Other: IRolePermit;
}

export interface IMyResourceFilter {
	MyUploads: boolean,
	SharedWithMe: boolean,
	MyFavorites: boolean
}

export interface IMyResource {
	ResourceFrom: string,
	ResourceId : string,
	Title: string,
	Description: string,
	Url: string,
	ThumbnailUrl: string,
	LargeThumbnailUrl: string,
	MapObjectId: string,
	PersonRole: string,
	PersonId: string,
	CreatedTime: string
}

export interface IFile {
	GUID: string,
	File: any,
	Name: string,
	Type: string,
	Size: string,
	Ext: string,
	Icon: string
}

export interface IUploadUrl {
	Title: string,
	Description: string,
	URL: string,
	StandardGuid: string,
	Share: boolean,
	Priority: boolean,
	Thumbnail: IFile
}

export interface IUploadFile {
	Title: string,
	Description: string,
	StandardGuid: string,
	Share: boolean,
	Priority: boolean,
	Resource: IFile
}


@Injectable({
	providedIn: 'root'
})
export class ResourceAPIService {
	private url = AppSettingService.appConfig.JourneyAPIUrl + "MyResource";

	constructor(
		private cacheSrv: CacheService,
		private mediaAPI: MediaAPIService,
		private userInfo: UserInfoService,
		private http: HttpClient
	) {
		this.getUserAllResources();
	}	

	private myResource = {
		personGuid: "" as string,
		cache: [] as any[]
	};

	private getUserAllResources(): Rx.Observable<any> {
		if (this.userInfo.getProfile().PersonGuid !== this.myResource.personGuid) {
			this.myResource.cache = [];
		}

		if (this.userInfo.isAnonymous() || this.myResource.cache.length > 0) {
			return Rx.of(this.myResource.cache);
		}
		else {
			return this.getSubmittedItems("", 10000, 0).pipe(tap(
				response => {
					this.myResource.personGuid = this.userInfo.getProfile().PersonGuid;
					this.myResource.cache = response;
				}
			));
		}
	}

	getSubmittedItems(mapObjectId: string, limit: number, offset: number): Rx.Observable<any> {
		let filter: IMyResourceFilter = {
			MyUploads: true,
			SharedWithMe: false,
			MyFavorites: false
		}
		return this.getResources(mapObjectId, filter, limit, offset);
	}

	getSubmittedItemCount(): Rx.Observable<number> {
		return this.getUserAllResources().pipe(map(
			response => {
				if (response) {
					return response.length;
				}
				else {
					return 0;
				}
			}
		));
	}

	getSubmittedItemCountByMapObject(mapObjectId: string): Rx.Observable<number> {
		return this.getUserAllResources().pipe(map(
			response => {
				if (response) {
					let xx = response.filter((x: any) => x.MapObjectId.toLowerCase() === mapObjectId.toLowerCase());
					return xx.length;
				}
				else {
					return 0;
				}

			}
		));
	}

	getResources(mapObjectId: string, filter: IMyResourceFilter, limit: number, offset: number): Rx.Observable<IMyResource[]> {
		let f: string[] = [];
		if (filter.MyUploads) {
			f.push("MyUploads");
		}
		if (filter.MyFavorites) {
			f.push("MyFavorites");
		}
		if (filter.SharedWithMe) {
			f.push("SharedWithMe");
		}

		return this.http.get<any>(
			this.url + "/User?mapObjectId=" + mapObjectId + "&filters=" + f.join(",") + '&offset=' + offset.toString() + '&limit=' + limit.toString(),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	submitUrl(myUrl: IUploadUrl): Rx.Observable<any> {
		this.myResource.cache = [];
		let formData: FormData = new FormData();
		formData.append('Title', myUrl.Title);
		formData.append('Description', myUrl.Description);
		formData.append('StandardGuid', myUrl.StandardGuid);
		formData.append('URL', myUrl.URL);
		formData.append('Share', myUrl.Share.toString());
		formData.append('Priority', myUrl.Priority.toString());
		if (myUrl.Thumbnail != null) {
			formData.append('Thumbnail', myUrl.Thumbnail.File);
		}
		return this.http.post<any>(
			this.url + "/User/SubmitUrl",
			formData,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	submitFile(myFile: IUploadFile): Rx.Observable<any> {
		this.myResource.cache = [];
		let formData: FormData = new FormData();
		formData.append('Title', myFile.Title);
		formData.append('Description', myFile.Description);
		formData.append('StandardGuid', myFile.StandardGuid);
		formData.append('Share', myFile.Share.toString());
		formData.append('Priority', myFile.Priority.toString());
		formData.append('File', myFile.Resource.File);
		return this.http.post<any>(
			this.url + "/User/SubmitFile",
			formData,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	updateUserResourceShare(resourceId: string, priority: boolean): Rx.Observable<IRolePermit> {
		return this.http.patch<any>(
			this.url + "/User/" + encodeURIComponent(resourceId) + "/Share/" + encodeURIComponent(priority),
			null,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	updateUserResourcePriority(resourceId: string, priority: boolean): Rx.Observable<IRolePermit> {
		return this.http.patch<any>(
			this.url + "/User/" + encodeURIComponent(resourceId) + "/Priority/" + encodeURIComponent(priority),
			null,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	updateUserResourceDescription(resourceId: string, description: string, resourcePersonId: string): Rx.Observable<IRolePermit> {
		return this.http.patch<any>(
			this.url + "/User/" + encodeURIComponent(resourceId) + "/Description",
			{ Description: description, ResourcePersonId: resourcePersonId },
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	deleteUserResource(resourceId: string): Rx.Observable<IRolePermit> {
		return this.http.delete<any>(
			this.url + "/User/" + encodeURIComponent(resourceId),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getResourcePermit(reload: boolean = false): Rx.Observable<IResourcePermit> {
		let l = this.url + "/User/ResourcePermit";
		if (reload) {
			return this.http.get<any>(
				l,
				{
					withCredentials: true,
					headers: { 'Authorization': this.userInfo.authHeader(false) }
				}
			).pipe(tap(() => this.cacheSrv.remove(l)));
		}
		else {
			return this.cacheSrv.get<IResourcePermit>(false, l);
		}
	}

	getRolePermit(): Rx.Observable<IRolePermit> {
		if (this.userInfo.isAnonymous()) {
			return Rx.of<IRolePermit>({
				CanUpload: false,
				CanShare: false
			});
		}
		else {
			return this.getResourcePermit().pipe(map(
				(response: IResourcePermit) => {
					let rolePermit: IRolePermit;

					switch (this.userInfo.getProfile().HighestRole) {
						case "SystemAdmin":
							rolePermit = response.SystemAdmin;
							break;
						case "ZoneAdmin":
							rolePermit = response.ZoneAdmin;
							break;
						case "SchoolAdmin":
							rolePermit = response.SchoolAdmin;
							break;
						case "Teacher":
							rolePermit = response.Teacher;
							break;
						case "Student":
							rolePermit = response.Student;
							break;
						case "Other":
							rolePermit = response.Other;
							break;
						default:
							rolePermit.CanShare = false;
							rolePermit.CanUpload = false;
					}
					return rolePermit;
				}
			));
		}
	}

	saveResourcePermit(permit: IResourcePermit): Rx.Observable<any> {
		return this.http.post<any>(
			this.url + "/User/ResourcePermit",
			permit,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	//#region public resource
	search(standardId: string, limit: number, offset: number): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/Public/" + standardId + '?offset=' + offset.toString() + '&limit=' + limit.toString()
		);
	}

	getSearchTotal(standardId: string): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/Public/" + standardId + "/Total"
		);
	}
	//#endregion public resource
}

